import { useEffect, useState } from 'react';

import { RecursivePartial } from '@beefree.io/sdk/dist/types/utils';
import { FormControlLabel, Stack, Switch } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import omit from 'lodash/omit';
import { DateTime } from 'luxon';

import { SwitchInputLabel } from 'domains/core/components/InputLabel';
import { Value } from 'domains/core/components/Select';
import {
    MIN_SUBSTRING_CONTAINS_LENGTH,
    RelativeDateRangeDirection,
    RelativeDateRange as RelativeDateRangeType,
    RelativeDateRangeValueAndUnit,
    SegmentFilter,
    SegmentFilterDemographicFields,
    SegmentFilterEventCondition,
    SegmentFilterEventFields,
    SegmentFilterState,
    isDateRangeOperator as isDateRangeOperatorType,
    isDateRangeType,
    isMultiInputOperator as isMultiInputOperatorType,
    isPercentileOperator as isPercentileOperatorType,
    isRelativeDateRangeType,
    isStringContainsOperator as isStringContainsOperatorType,
} from 'domains/segments/types';
import { isRelativeDateRangeLast, isRelativeDateRangeNext, isRelativeDateRangeToday } from 'domains/segments/utils';
import useContactFieldsTopValues from 'hooks/queries/useContactFieldsTopValues';
import { SQLType } from 'models/enums';

import { getInitialDateRangeValue, getInitialDateValue, getRelativeDateRangeOffset } from '../utils';

import AutocompleteInput from './AutocompleteInput';
import DatePickerInput from './DatePickerInput';
import DateRangePickerInputs from './DateRangePickerInputs';
import DateTimePickerInput from './DateTimePickerInput';
import DateTimeRangePickerInputs from './DateTimeRangePickerInputs';
import PercentageInput from './PercentageInput';
import RelativeDateRange from './RelativeDateRange';
import TextFieldInput from './TextFieldInput';

const flexWrap = { flexWrap: 'wrap' };
const fullWidth = { width: '15rem' };
const rowGap = { rowGap: 2 };
const flexWrapFullWrap = { ...flexWrap, ...fullWidth };

const dateRangeSx = {
    textFieldSx: { ...fullWidth, marginLeft: '0!important' },
    stackSx: { ...flexWrapFullWrap, ...rowGap },
};
const datetimeRangePickerSx = {
    dateTimePickerSx: fullWidth,
    stackSx: { ...flexWrapFullWrap, ...rowGap },
};
const relativeDateRangePickerSx = {
    daysTextFieldSx: fullWidth,
    relativeDateRangeSelectSx: fullWidth,
    relativeDateRangeStackSx: flexWrapFullWrap,
};
const relativeDateTimeRangePickerSx = {
    dateTimeSelectSx: { width: '9rem' },
    dateTimeTextFieldSx: { width: '5rem' },
    relativeDateRangeSelectSx: fullWidth,
    relativeDateRangeStackSx: flexWrapFullWrap,
};

export type RelativeDateRangeArgs = {
    relativeDateRange?: RecursivePartial<RelativeDateRangeType>;
    unit?: RelativeDateRangeValueAndUnit['unit'];
};

export type Props = {
    activeCondition?: SegmentFilterEventCondition;
    filterOption: SegmentFilter;
    filterState: SegmentFilterState<SegmentFilterEventFields | SegmentFilterDemographicFields>;
    handleDatetimeError: (filterId: string, error: boolean) => void;
    handleChangeValue: (value: any) => void;
    handleChangeAutocompleteInputValue: (autocompleteInputValue: string, isMultiple: boolean) => void;
    handleChangeAutocompleteValue: (autocompleteValue: any, resetPreview?: boolean) => void;
    handleChangeAutocompleteValues?: (autocompleteValue?: any, resetPreview?: boolean) => void;
    isResponsive?: boolean;
};

const FilterValueInput = ({
    activeCondition,
    filterOption,
    filterState,
    handleDatetimeError,
    handleChangeValue,
    handleChangeAutocompleteInputValue,
    handleChangeAutocompleteValue,
    isResponsive,
}: Props) => {
    const displayName = filterOption?.displayName;
    const sqlType = filterOption?.sqlType;
    const valuesRef = filterOption?.valuesRef;

    const { data: contactFieldsTopValues } = useContactFieldsTopValues(valuesRef);

    const contactFieldsTopValuesOptions: Value[] = contactFieldsTopValues?.map(({ value, displayValue }) => ({
        label: displayValue,
        value,
    }));

    const filterId = filterState.id.toString();
    const operator = activeCondition ? activeCondition.operator : filterState.operator;
    const value = activeCondition ? activeCondition.value : filterState.categorySpecificFields?.value;
    const autocompleteInputValue = activeCondition
        ? activeCondition?.autocompleteInputValue
        : filterState.autocompleteInputValue;
    const autocompleteValue = activeCondition?.autocompleteValue || filterState.autocompleteValue;

    const isDateRangeOperator = isDateRangeOperatorType(operator);
    const isPercentileOperator = isPercentileOperatorType(operator);
    const isMultiInputOperator = isMultiInputOperatorType(operator);
    const isStringContainsOperator = isStringContainsOperatorType(operator);

    const isSubstringContainsTooShort =
        isStringContainsOperator &&
        typeof value === 'string' &&
        value.length > 0 &&
        value.length < MIN_SUBSTRING_CONTAINS_LENGTH;
    const substringContainsHelperText = isSubstringContainsTooShort
        ? `Enter at least ${MIN_SUBSTRING_CONTAINS_LENGTH} characters`
        : undefined;

    // contains/does not contain is a substring search, so suggesting whole known values from the
    // top-values dropdown doesn't match the "type a fragment" mental model - always use plain text.
    const isAutocomplete = !isStringContainsOperator && (contactFieldsTopValues?.length || isMultiInputOperator);
    const isDate = sqlType === SQLType.DATE;
    const isDateTime = sqlType === SQLType.TIMESTAMP_WITH_TIME_ZONE;

    const isDateTimeRangeValue = isDateRangeType(value);
    const isRelativeDateRangeValue = isRelativeDateRangeType(value);

    const durationUnit = isRelativeDateRangeValue ? value.duration.unit : null;
    const durationValue = isRelativeDateRangeValue ? value.duration.value : null;

    const valueDateTime = (typeof value === 'string' && DateTime.fromISO(value)) ?? null;
    const valueDateTimeStart = isDateTimeRangeValue ? DateTime.fromISO(value.start) : null;
    const valueDateTimeEnd = isDateTimeRangeValue ? DateTime.fromISO(value.end) : null;

    let valueRelativeDateRangeDirection: RelativeDateRangeDirection;
    if (isRelativeDateRangeLast(value) || isRelativeDateRangeToday(value)) {
        valueRelativeDateRangeDirection = RelativeDateRangeDirection.THE_LAST;
    } else if (isRelativeDateRangeNext(value)) {
        valueRelativeDateRangeDirection = RelativeDateRangeDirection.THE_NEXT;
    }

    const [isRelativeDateRangeChecked, setIsRelativeDateRangeChecked] = useState(isRelativeDateRangeValue);
    const [relativeDateRangeDirection, setRelativeDateRangeDirection] = useState(valueRelativeDateRangeDirection);

    // Prevents the need for the user to re-select the default date & time for enabling the preview button
    useEffect(() => {
        const defaultDate = DateTime.fromISO(getInitialDateValue(value));
        const defaultDateStart = DateTime.fromISO(getInitialDateRangeValue(value, 'start'));
        const defaultDateEnd = DateTime.fromISO(getInitialDateRangeValue(value, 'end'));

        if (isDateRangeOperator && isRelativeDateRangeChecked) {
            const defaultValue = {
                offset: getRelativeDateRangeOffset(valueRelativeDateRangeDirection, durationValue),
                duration: {
                    value: durationValue,
                    unit: durationUnit ?? 'day',
                },
            };
            handleRelativeDateRangeChange({ relativeDateRange: defaultValue });
            setRelativeDateRangeDirection(valueRelativeDateRangeDirection);
        } else if (isDateRangeOperator && isDate) {
            handleDateRangeChange([defaultDateStart, defaultDateEnd]);
        } else if (isDateRangeOperator && isDateTime) {
            const defaultValue = {
                start: defaultDateStart.toISO(),
                end: defaultDateEnd.toISO(),
            };
            handleChangeValue(defaultValue);
        } else if (isDate || isDateTime) {
            handleDateTimeChange(defaultDate, isDate);
            setIsRelativeDateRangeChecked(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDate, isDateRangeOperator, isDateTime, isRelativeDateRangeChecked, isRelativeDateRangeValue, operator]);

    // Guard against undefined filterOption - must be after all hooks
    if (!filterOption) {
        return null;
    }

    const handleDateRangeChange = (dateRange: DateRange<DateTime>) => {
        const [dateRangeStart, dateRangeEnd] = dateRange;

        // Check for null values before accessing properties
        const isValidDates = dateRangeStart?.isValid && dateRangeEnd?.isValid;
        const isValidDateStartEnd = dateRangeStart && dateRangeEnd && dateRangeStart <= dateRangeEnd;
        const isValidDateRange = isValidDates && isValidDateStartEnd;

        // Only proceed if both dates exist
        if (!dateRangeStart || !dateRangeEnd) {
            handleDatetimeError(filterId, true);
            return;
        }

        const newValue = {
            start: dateRangeStart.toFormat('yyyy-MM-dd'),
            end: dateRangeEnd.toFormat('yyyy-MM-dd'),
        };

        handleChangeValue(newValue);
        handleDatetimeError(filterId, !isValidDateRange);
    };

    const handleDateTimeRangeChange = (datetime: DateTime, isStart: boolean) => {
        const isValidDate = datetime?.isValid;
        const isValidDateStartEnd = isStart
            ? datetime && valueDateTimeEnd && datetime < valueDateTimeEnd
            : datetime && valueDateTimeStart && datetime > valueDateTimeStart;
        const isValidDateRange = isValidDate && isValidDateStartEnd;

        const newValue = {
            ...(isDateTimeRangeValue && value),
            ...(isStart ? { start: datetime.toISO() } : { end: datetime.toISO() }),
        };

        handleChangeValue(newValue);
        handleDatetimeError(filterId, !isValidDateRange);
    };

    const handleIsRelativeDateRangeCheckedChange = () => setIsRelativeDateRangeChecked(!isRelativeDateRangeChecked);

    const handleRelativeDateRangeChange = ({ relativeDateRange, unit }: RelativeDateRangeArgs) => {
        // Spread in existing offset/duration keys as both don't have to be provided.
        const existingValues = omit(value as object, ['start', 'end']) as RelativeDateRangeType;

        const newOffset = {
            offset: {
                value: relativeDateRange?.offset ? relativeDateRange.offset.value : existingValues.offset.value,
                unit: unit ?? existingValues?.offset?.unit,
            },
        };
        const newDuration = {
            duration: {
                value: relativeDateRange?.duration ? relativeDateRange.duration.value : existingValues.duration.value,
                unit: unit ?? existingValues?.duration?.unit,
            },
        };

        const newValue = {
            ...existingValues,
            ...newOffset,
            ...newDuration,
        };

        handleChangeValue(newValue);
    };

    const handleDateTimeChange = (datetime: DateTime, isDate: boolean) => {
        const isValidDate = datetime.isValid;
        const newValue = isDate ? datetime.toFormat('yyyy-MM-dd') : datetime.toISO();

        handleChangeValue(newValue);
        handleDatetimeError(filterId, !isValidDate);
    };

    // TODO: combine handling date, datetime, date range, datetime range, and relative date range into single component with sub components
    // TODO: make this conditional render for date range & relative date range into a sub component
    if (isDate && isDateRangeOperator) {
        return (
            <Stack rowGap={1}>
                {isRelativeDateRangeChecked ? (
                    <RelativeDateRange
                        direction={relativeDateRangeDirection}
                        duration={durationValue}
                        handleRelativeDateRangeChange={handleRelativeDateRangeChange}
                        setDirection={setRelativeDateRangeDirection}
                        timeframe={durationUnit}
                        {...(isResponsive && { styles: relativeDateRangePickerSx })}
                    />
                ) : (
                    <div data-testid="date-range-picker">
                        <DateRangePickerInputs
                            onDateRangeChange={handleDateRangeChange}
                            valueStart={valueDateTimeStart}
                            valueEnd={valueDateTimeEnd}
                            {...(isResponsive && { styles: dateRangeSx })}
                        />
                    </div>
                )}
                <FormControlLabel
                    control={
                        <Switch
                            color="secondary"
                            size="small"
                            checked={isRelativeDateRangeChecked}
                            onChange={handleIsRelativeDateRangeCheckedChange}
                            sx={{ mx: 1 }}
                        />
                    }
                    label={
                        <SwitchInputLabel
                            label="Use relative date range"
                            labelIconTooltipText="Relative dates update dynamically as the calendar changes."
                        />
                    }
                    sx={{ mr: 0 }}
                />
            </Stack>
        );
    }

    if (isDateTime && isDateRangeOperator) {
        return (
            <Stack rowGap={1}>
                {isRelativeDateRangeChecked ? (
                    <RelativeDateRange
                        direction={relativeDateRangeDirection}
                        duration={durationValue}
                        handleRelativeDateRangeChange={handleRelativeDateRangeChange}
                        isDateTime
                        setDirection={setRelativeDateRangeDirection}
                        timeframe={durationUnit}
                        {...(isResponsive && { styles: relativeDateTimeRangePickerSx })}
                    />
                ) : (
                    <DateTimeRangePickerInputs
                        onChangeValueStart={(dateTimeStart) => handleDateTimeRangeChange(dateTimeStart, true)}
                        onChangeValueEnd={(dateTimeEnd) => handleDateTimeRangeChange(dateTimeEnd, false)}
                        valueStart={valueDateTimeStart}
                        valueEnd={valueDateTimeEnd}
                        {...(isResponsive && { styles: datetimeRangePickerSx })}
                    />
                )}
                <FormControlLabel
                    control={
                        <Switch
                            color="secondary"
                            size="small"
                            checked={isRelativeDateRangeChecked}
                            onChange={handleIsRelativeDateRangeCheckedChange}
                            sx={{ mx: 1 }}
                        />
                    }
                    label={
                        <SwitchInputLabel
                            label="Use relative date range"
                            labelIconTooltipText="Relative dates update dynamically as the calendar changes."
                        />
                    }
                    sx={{ mr: 0 }}
                />
            </Stack>
        );
    }

    if (isDate) {
        return (
            <DatePickerInput
                onChangeDate={(value) => handleDateTimeChange(value, true)}
                value={valueDateTime}
                {...(isResponsive && { sx: fullWidth })}
            />
        );
    }

    if (isDateTime) {
        return (
            <DateTimePickerInput
                onChange={(value) => handleDateTimeChange(value, false)}
                value={valueDateTime}
                {...(isResponsive && { sx: fullWidth })}
            />
        );
    }

    if (isPercentileOperator) {
        return (
            <PercentageInput
                placeholderName={displayName}
                value={value}
                onChange={handleChangeValue}
                {...(isResponsive && { sx: fullWidth })}
            />
        );
    }

    if (isAutocomplete) {
        return (
            <AutocompleteInput
                autocompleteInputValue={autocompleteInputValue}
                autocompleteValue={isMultiInputOperator ? autocompleteValue : value}
                freeSolo
                label="Value"
                multiple={isMultiInputOperator}
                onChange={handleChangeAutocompleteValue}
                onInputChange={(autocompleteInputValue) =>
                    handleChangeAutocompleteInputValue(autocompleteInputValue, isMultiInputOperator)
                }
                options={contactFieldsTopValuesOptions}
                placeholderName={displayName}
                type={sqlType}
                {...(isResponsive && {
                    styles: { sxAutocomplete: fullWidth },
                })}
            />
        );
    }

    return (
        <TextFieldInput
            error={isSubstringContainsTooShort}
            helperText={substringContainsHelperText}
            onChange={handleChangeValue}
            placeholderName={displayName}
            sqlType={sqlType}
            value={value}
            {...(isResponsive && { sx: fullWidth })}
        />
    );
};

export default FilterValueInput;
